import { Dispatch, FC, SetStateAction, useEffect } from 'react'
import { produce } from 'immer'
import {
  Card,
  CardBody,
  CardHeader,
  CardSubtitle,
  CardTitle,
  GridLayout,
  GridLayoutItem
} from '@progress/kendo-react-layout'
import {
  Input,
  InputChangeEvent,
  NumericTextBox,
  NumericTextBoxChangeEvent,
  TextArea,
  TextAreaChangeEvent
} from '@progress/kendo-react-inputs'
import { Contents } from '../../../components/layouts'
import {
  BranchSelect,
  EvaluationSelect,
  ProcurementStatusSelect,
  ObtainHistorySelect,
  ConstrFormSelect,
  FiscalYearDropDownList,
  ConstrTypeSelect,
  BuildingUsecaseDropDownList,
  StructureTypeSelect,
  AvailabilityStatusSelect,
  UnderGroundConstrMethodSelect,
  FiscalMonthDropDownList,
  DesignClassificationSelect,
  EffortLevelSelect,
  PrefectureSelect
} from '../../../components/parts'
import { BuildingInfoPut } from '@gateway.Building'
import { useAppSelector } from '../../../stores'
import { Tooltip } from '@progress/kendo-react-tooltip'

export interface BuildingRegistrationProps {
  setBuildingRegistration: Dispatch<SetStateAction<BuildingInfoPut>>
  buildingRegistration: BuildingInfoPut
}

export const BuildingRegistration: FC<BuildingRegistrationProps> = ({
  buildingRegistration,
  setBuildingRegistration
}) => {
  const common = useAppSelector((x) => x.common)
  const codeNameEaluations = common.initialData.codeNames('HY01')
  const codeNameBranches = common.initialData.codeNames('SI01')
  const codeNameEffortLevel = common.initialData.codeNames('TO02')
  const codeNameDesignKbnNm = common.initialData.codeNames('SE04')

  /** 延床面積㎡ 延床面積坪 */
  const handleDispTotalFloorAreaM2AndTsubo = (e: NumericTextBoxChangeEvent) => {
    setBuildingRegistration(
      produce((draft) => {
        draft.dispTotalFloorAreaM2 = e.target.value ?? 0
        draft.totalFloorAreaM2 = e.target.value ?? 0
        draft.dispTotalFloorAreaTsubo = Number(((e.target.value ?? 0) * 0.3025).toFixed(3))
        draft.totalFloorAreaTsubo = Number(((e.target.value ?? 0) * 0.3025).toFixed(3))
      })
    )
  }

  /** 敷地面積㎡ 敷地面積坪 */
  const handleDispSiteAreaM2AndTsubo = (e: NumericTextBoxChangeEvent) => {
    setBuildingRegistration(
      produce((draft) => {
        draft.dispSiteAreaM2 = e.target.value ?? 0
        draft.siteAreaM2 = e.target.value ?? 0
        draft.dispSiteAreaTsubo = Number(((e.target.value ?? 0) * 0.3025).toFixed(3))
        draft.siteAreaTsubo = Number(((e.target.value ?? 0) * 0.3025).toFixed(3))
      })
    )
  }

  /** branchCdList 支店 **/
  const handleBranchCdList = (valueList: string[]) => {
    setBuildingRegistration(
      produce((draft) => {
        draft.branchCd = valueList[0]

        const name = codeNameBranches.find(
          (codeName) => codeName.CODE === valueList[0]
        )?.NAME
        draft.branchNm = name ?? ''
      })
    )
  }

  /** 評価値 */
  const handleEvaluationValue = (val: string[]) => {
    setBuildingRegistration(
      produce((draft) => {
        draft.evaluationValueCd = val[0]

        const name = codeNameEaluations.find(
          (codeName) => codeName.CODE === val[0]
        )?.NAME
        draft.evaluationValue = name ?? ''
      })
    )
  }

  /** 選択と集中対象 */
  const handleChoiceAndConcentrationTarget = (val: string[]) => {
    setBuildingRegistration(
      produce((draft) => {
        draft.dispChoiceAndConcentrationTarget = Number(val[0])
        draft.choiceAndConcentrationTarget = Number(val[0])
      })
    )
  }

  /** 工事名 */
  const handleConstructionNm = (event: InputChangeEvent) => {
    setBuildingRegistration(
      produce((draft) => {
        draft.dispConstructionNm = event.value
        draft.constructionNm = event.value
      })
    )
  }

  /** 調達状況 */
  const handleProcurementSts = (val: string[]) => {
    setBuildingRegistration(
      produce((draft) => {
        draft.procurementSts = val[0]
      })
    )
  }

  /** 入手経緯 */
  const handleObtainHistoryCd = (val: string[]) => {
    setBuildingRegistration(
      produce((draft) => {
        draft.dispObtainHistoryCd = val[0]
        draft.obtainHistoryCd = val[0]
      })
    )
  }

  /** 施工形式 */
  const handleConstructionFormCd = (val: string[]) => {
    setBuildingRegistration(
      produce((draft) => {
        draft.dispConstructionFormCd = val[0]
        draft.constructionFormCd = val[0]
      })
    )
  }

  /** 当社持分比 */
  const handleOurCompanyEquityRatio = (event: NumericTextBoxChangeEvent) => {
    setBuildingRegistration(
      produce((draft) => {
        draft.dispOurCompanyEquityRatio = event.value ?? null as unknown as number
        draft.ourCompanyEquityRatio = event.value ?? null as unknown as number
      })
    )
  }

  /** 取組レベル */
  const handleEffortLevel = (val: string[]) => {
    setBuildingRegistration(
      produce((draft) => {
        draft.dispEffortLevelCd = val[0]
        draft.effortLevelCd = val[0]

        const name = codeNameEffortLevel.find(
          (codeName) => codeName.CODE === val[0]
        )?.NAME
        draft.dispEffortLevel = name ?? ''
        draft.effortLevel = name ?? ''
      })
    )
  }

  /** コアトップ案件フラグ */
  const handleCoreTopFlg = (val: string[]) => {
    setBuildingRegistration(
      produce((draft) => {
        draft.dispCoreTopFlg = Number(val[0])
        draft.coreTopFlg = Number(val[0])
      })
    )
  }

  /** 入手年度 */
  const handleObtainFy = (year: number) => {
    setBuildingRegistration(
      produce((draft) => {
        draft.dispObtainFY = year.toString()
        draft.obtainFY = year.toString()
      })
    )
  }

  /** 都道府県名 */
  const handlePrefecture = (val: string[]) => {
    setBuildingRegistration(
      produce((draft) => {
        draft.dispPrefecture = val[0]
        draft.prefecture = val[0]
      })
    )
  }

  /** 市区町村名 */
  const handleCityName = (event: InputChangeEvent) => {
    setBuildingRegistration(
      produce((draft) => {
        draft.dispCity = event.value
        draft.city = event.value
      })
    )
  }

  /** 工事科目コード */
  const handleConstructionSubjectCd = (event: NumericTextBoxChangeEvent) => {
    setBuildingRegistration(
      produce((draft) => {
        draft.dispConstructionSubjectCd =
          event.value !== null
            ? String(event.value)
            : null as unknown as string
        draft.constructionSubjectCd =
            event.value !== null
              ? String(event.value)
              : null as unknown as string
      })
    )
  }

  /** 総工事価格 */
  const handleTotalCost = (event: NumericTextBoxChangeEvent) => {
    setBuildingRegistration(
      produce((draft) => {
        draft.dispTotalCost = event.value ?? null as unknown as number
        draft.totalCost = event.value ?? null as unknown as number
      })
    )
  }

  /** 当社概算工事価格 */
  const handleOurCompanyTotalCost = (event: NumericTextBoxChangeEvent) => {
    setBuildingRegistration(
      produce((draft) => {
        draft.dispOurCompanyTotalCost = event.value ?? null as unknown as number
        draft.ourCompanyTotalCost = event.value ?? null as unknown as number
      })
    )
  }

  /** 工事種別 */
  const handleConstrucionCd = (val: string[]) => {
    setBuildingRegistration(
      produce((draft) => {
        draft.dispConstructionCd = val[0]
        draft.constructionCd = val[0]
      })
    )
  }

  /** 建物用途 */
  const handleArchitectureUse = (codeValue: string) => {
    setBuildingRegistration(
      produce((draft) => {
        draft.architectureUse = codeValue
      })
    )
  }

  /** 構造・地下 */
  const handleDispStructureUnderground = (val: string[]) => {
    setBuildingRegistration(
      produce((draft) => {
        draft.dispStructureUnderGround = val[0]
        draft.structureUnderGround = val[0]
      })
    )
  }

  /** 構造・地上 */
  const handleDispStructureGround = (val: string[]) => {
    setBuildingRegistration(
      produce((draft) => {
        draft.dispStructureGround = val[0]
        draft.structureGround = val[0]
      })
    )
  }

  /** 階数・地下 */
  const handleDispFloorUnderGround = (event: any) => {
    setBuildingRegistration(
      produce((draft) => {
        draft.dispFloorUnderGround = event.value
        draft.floorUnderGround = event.value
      })
    )
  }

  /** 階数・地上 */
  const handleDispFloorGround = (event: NumericTextBoxChangeEvent) => {
    setBuildingRegistration(
      produce((draft) => {
        draft.dispFloorGround = event.value
        draft.floorGround = event.value
      })
    )
  }

  /** 階数・塔屋 */
  const handleDispFloorPenthouse = (event: NumericTextBoxChangeEvent) => {
    setBuildingRegistration(
      produce((draft) => {
        draft.dispFloorPenthouse = event.value
        draft.floorPenthouse = event.value
      })
    )
  }

  /** 設備工事 */
  const handleDispBuildingServicesWorkFlg = (val: string[]) => {
    setBuildingRegistration(
      produce((draft) => {
        draft.dispBuildingServicesWorkFlg = Number(val[0])
        draft.buildingServicesWorkFlg = Number(val[0])
      })
    )
  }

  /** 杭打 */
  const handlePiling = (val: string[]) => {
    setBuildingRegistration(
      produce((draft) => {
        draft.piling = Number(val[0])
      })
    )
  }

  /**  免震 */
  const handleSeismicIsolation = (val: string[]) => {
    setBuildingRegistration(
      produce((draft) => {
        draft.seismicIsolation = Number(val[0])
      })
    )
  }

  /**  順打逆打 */
  const handleTopDown = (val: string[]) => {
    setBuildingRegistration(
      produce((draft) => {
        draft.topDownFlg = Number(val[0])
      })
    )
  }

  /** 工法 */
  const handleSpecialConstructionMethodCd = (event: InputChangeEvent) => {
    setBuildingRegistration(
      produce((draft) => {
        draft.dispSpecialConstructionMethod = event.value
        draft.specialConstructionMethod = event.value
      })
    )
  }

  /** 着工年 */
  const handleStartY = (year: number) => {
    setBuildingRegistration(
      produce((draft) => {
        draft.startY = year.toString()
      })
    )
  }

  /** 着工月 */
  const handleStartM = (month: number) => {
    setBuildingRegistration(
      produce((draft) => {
        draft.startM = month.toString()
      })
    )
  }

  /** 竣工年 */
  const handleCompletionY = (year: number) => {
    setBuildingRegistration(
      produce((draft) => {
        draft.completionY = year.toString()
      })
    )
  }

  /** 竣工月 */
  const handleCompletionM = (month: number) => {
    setBuildingRegistration(
      produce((draft) => {
        draft.completionM = month.toString()
      })
    )
  }

  /**  工期 */
  const handleSchedule = (event: NumericTextBoxChangeEvent) => {
    setBuildingRegistration(
      produce((draft) => {
        draft.dispSchedule = event.value
        draft.schedule = event.value
      })
    )
  }

  /** 備考 */
  const handleRemark = (event: TextAreaChangeEvent) => {
    setBuildingRegistration(
      produce((draft) => {
        draft.remark = event.value
      })
    )
  }

  /** 本社営業部名 */
  const handleHqSalesSectionNm = (event: InputChangeEvent) => {
    setBuildingRegistration(
      produce((draft) => {
        draft.dispHqSalesSectionNm = event.value
        draft.hqSalesSectionNm = event.value
      })
    )
  }

  /** 本社営業担当者名 */
  const handleHqSalesPersonNm = (event: InputChangeEvent) => {
    setBuildingRegistration(
      produce((draft) => {
        draft.dispHqSalesPersonNm = event.value
        draft.hqSalesPersonNm = event.value
      })
    )
  }

  /** 本社営業担当者連絡先 */
  const handleHqSalesPersonContacts = (event: InputChangeEvent) => {
    setBuildingRegistration(
      produce((draft) => {
        draft.dispHqSalesPersonContacts = event.value
        draft.hqSalesPersonContacts = event.value
      })
    )
  }

  /** 支店営業部名 */
  const handleBranchSalesSectionNm = (event: InputChangeEvent) => {
    setBuildingRegistration(
      produce((draft) => {
        draft.dispBranchSalesSectionNm = event.value
        draft.branchSalesSectionNm = event.value
      })
    )
  }

  /** 支店営業担当者名 */
  const handleBranchSalesPersonNm = (event: InputChangeEvent) => {
    setBuildingRegistration(
      produce((draft) => {
        draft.dispBranchSalesPersonNm = event.value
        draft.branchSalesPersonNm = event.value
      })
    )
  }

  /** 支店営業担当者連絡先 */
  const handleBranchSalesPersonContacts = (event: InputChangeEvent) => {
    setBuildingRegistration(
      produce((draft) => {
        draft.dispBranchSalesPersonContacts = event.value
        draft.branchSalesPersonContacts = event.value
      })
    )
  }

  /** 発注者名 */
  const handleCustomerNm = (event: InputChangeEvent) => {
    setBuildingRegistration(
      produce((draft) => {
        draft.dispCustomerNm = event.value
        draft.customerNm = event.value
      })
    )
  }

  /** 共同発注者名 */
  const handleCollabCustomerNm = (event: InputChangeEvent) => {
    setBuildingRegistration(
      produce((draft) => {
        draft.dispCollabCustomerNm = event.value
      })
    )
  }

  /** 設計区分 */
  const handleDesignKbnNm = (val: string[]) => {
    setBuildingRegistration(
      produce((draft) => {
        draft.dispDesignKbn = val[0]
        draft.designKbn = val[0]

        const name = codeNameDesignKbnNm.find(
          (codeName) => codeName.CODE === val[0]
        )?.NAME
        draft.dispDesignKbnNm = name ?? ''
        draft.designKbnNm = name ?? ''
      })
    )
  }

  /** 設計者 */
  const handleInputDesignerNm = (event: InputChangeEvent) => {
    setBuildingRegistration(
      produce((draft) => {
        draft.dispDesignerNm = event.value
        draft.designerNm = event.value
      })
    )
  }

  /** 修正コメント */
  const handleFixComment = (event: TextAreaChangeEvent) => {
    setBuildingRegistration(
      produce((draft) => {
        draft.fixComment = event.value
      })
    )
  }

  /** 工事部名 */
  const handleConstructionSectionNm = (event: InputChangeEvent) => {
    setBuildingRegistration(
      produce((draft) => {
        draft.dispConstructionSectionNm = event.value
        draft.constructionSectionNm = event.value
      })
    )
  }

  /** 所長担当者名 */
  const handleChiefPersonNm = (event: InputChangeEvent) => {
    setBuildingRegistration(
      produce((draft) => {
        draft.dispChiefPersonNm = event.value
        draft.chiefPersonNm = event.value
      })
    )
  }

  /** 所長担当者連絡先 */
  const handleChiefPersonContacts = (event: InputChangeEvent) => {
    setBuildingRegistration(
      produce((draft) => {
        draft.dispChiefPersonContacts = event.value
        draft.chiefPersonContacts = event.value
      })
    )
  }

  /** 作業所コード */
  const handleSagyosyoCd = (event: NumericTextBoxChangeEvent) => {
    setBuildingRegistration(
      produce((draft) => {
        draft.dispSagyosyoCd =
          event.value !== null
            ? String(event.value)
            : null as unknown as string
        draft.sagyosyoCd =
            event.value !== null
              ? String(event.value)
              : null as unknown as string
      })
    )
  }

  // 工期計算
  useEffect(() => {
    if (buildingRegistration.completionY !== '' && buildingRegistration.completionM !== '' && buildingRegistration.startY !== '' && buildingRegistration.startM !== '') {
      const monthesC = Number(buildingRegistration.completionY ?? 0) * 12 + (Number(buildingRegistration.completionM ?? 0) - 1)
      const monthesS = Number(buildingRegistration.startY ?? 0) * 12 + (Number(buildingRegistration.startM ?? 0) - 1)
      const diff = monthesC - monthesS + 1
      setBuildingRegistration(
        produce((draft) => {
          draft.dispSchedule = diff
          draft.schedule = diff
        })
      )
    }
  }, [buildingRegistration.completionY, buildingRegistration.completionM, buildingRegistration.startY, buildingRegistration.startM])

  return (
    <Contents>
      <>
        <Tooltip position={'right'} parentTitle={true} className={'pcy-tooltip'}>
          <Card orientation="horizontal">
            <CardHeader style={{ padding: '9.5px 6px', width: '16%' }}>
              <CardTitle style={{ marginTop: '0px' }}>
                <BranchSelect
                  onChange={handleBranchCdList}
                  style={{
                    fontWeight: 'bold',
                    fontSize: '1.5em',
                    width: '70%'
                  }}
                  value={[buildingRegistration.branchCd ?? '']}
                  popupSettings={{ className: 'pcy-dropdownlist', popupClass: 'pcy-dropdownlist-innner' }}
                  clear={true}
                  title={'支店（入力必須）'}
                  className={'pcy-disabled-field'}
                />
              </CardTitle>
              <CardSubtitle style={{ marginTop: '0px' }}>
                <EvaluationSelect
                  onChange={handleEvaluationValue}
                  style={{
                    fontSize: '1.25em',
                    fontWeight: 'bold',
                    width: '70%'
                  }}
                  value={[buildingRegistration.evaluationValueCd ?? '']}
                  popupSettings={{ className: 'pcy-dropdownlist', popupClass: 'pcy-dropdownlist-innner' }}
                  clear={true}
                  title={'評価値（入力必須）'}
                  className={'pcy-disabled-field'}
                />
              </CardSubtitle>
              <CardSubtitle style={{ marginTop: '0px' }}>
                <span style={{ fontWeight: 'bold', fontSize: '1em' }}>
                  選択と集中:
                </span>
                <AvailabilityStatusSelect
                  onChange={handleChoiceAndConcentrationTarget}
                  style={{
                    fontSize: '1.25em',
                    fontWeight: 'bold',
                    width: '35%'
                  }}
                  value={
                    buildingRegistration.dispChoiceAndConcentrationTarget !== undefined
                      ? [String(buildingRegistration.dispChoiceAndConcentrationTarget)]
                      : undefined
                  }
                  popupSettings={{ className: 'pcy-dropdownlist', popupClass: 'pcy-dropdownlist-innner' }}
                  clear={true}
                  title={'選択と集中'}
                  className={'pcy-disabled-field'}
                />
              </CardSubtitle>
              <CardSubtitle style={{ marginTop: '0px' }}>
                <span
                  style={{ fontSize: '1.25em', fontWeight: 'bold', color: 'red' }}
                >
                  新規
                </span>
              </CardSubtitle>
            </CardHeader>
            <CardBody style={{ padding: '9.5px 6px' }}>
              <GridLayout cols={[{ width: '73%' }, { width: '27%' }]}>
                <GridLayoutItem
                  col={1}
                  style={{ marginLeft: 10, marginRight: 10 }}
                >
                  <GridLayout>
                    <GridLayoutItem col={1} colSpan={3} row={1}>
                      <Input
                        onChange={handleConstructionNm}
                        style={{ fontWeight: 'bold', fontSize: '1.0em' }}
                        value={buildingRegistration.dispConstructionNm ?? ''}
                        type={'text'}
                        title={'工事名（入力必須）'}
                        placeholder={'工事名（入力必須）'}
                        className={'pcy-disabled-field'}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem col={1} row={2}>
                      <NumericTextBox
                        onChange={handleConstructionSubjectCd}
                        value={
                          buildingRegistration.dispConstructionSubjectCd !== null
                            ? Number(buildingRegistration.dispConstructionSubjectCd)
                            : null
                        }
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.8em',
                          width: '11%',
                          height: '80%'
                        }}
                        format={'#'}
                        spinners={false}
                        title={'工事科目コード'}
                        placeholder={'工事科目コード'}
                        className={'pcy-disabled-field'}
                      />
                      <ProcurementStatusSelect
                        onChange={handleProcurementSts}
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.7em',
                          width: '12%',
                          height: '80%'
                        }}
                        value={[buildingRegistration.procurementSts ?? '']}
                        popupSettings={{ className: 'pcy-dropdownlist', popupClass: 'pcy-dropdownlist-innner' }}
                        clear={true}
                        title={'調達状況'}
                        className={'pcy-disabled-field'}
                      />
                      <ObtainHistorySelect
                        onChange={handleObtainHistoryCd}
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.7em',
                          width: '10%',
                          height: '80%'
                        }}
                        value={[buildingRegistration.dispObtainHistoryCd ?? '']}
                        popupSettings={{ className: 'pcy-dropdownlist', popupClass: 'pcy-dropdownlist-innner' }}
                        clear={true}
                        title={'入手経緯'}
                        className={'pcy-disabled-field'}
                      />
                      <ConstrFormSelect
                        onChange={handleConstructionFormCd}
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.7em',
                          width: '18%',
                          height: '80%'
                        }}
                        value={[buildingRegistration.dispConstructionFormCd ?? '']}
                        popupSettings={{ className: 'pcy-dropdownlist', popupClass: 'pcy-dropdownlist-innner' }}
                        clear={true}
                        title={'施工形式'}
                        className={'pcy-disabled-field'}
                      />
                      <NumericTextBox
                        onChange={handleOurCompanyEquityRatio}
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.8em',
                          width: '9%',
                          height: '80%'
                        }}
                        value={buildingRegistration.dispOurCompanyEquityRatio}
                        max={100}
                        spinners={false}
                        title={'当社持分比'}
                        placeholder={'当社持分比'}
                        className={'pcy-disabled-field'}
                      />
                      <span style={{ fontWeight: 'bold', fontSize: '0.8em', width: '8%' }}>
                        %
                      </span>
                      <EffortLevelSelect
                        onChange={handleEffortLevel}
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.7em',
                          width: '15%',
                          height: '80%'
                        }}
                        value={[buildingRegistration.dispEffortLevelCd ?? '']}
                        popupSettings={{ className: 'pcy-dropdownlist', popupClass: 'pcy-dropdownlist-innner' }}
                        clear={true}
                        title={'取組レベル'}
                        className={'pcy-disabled-field'}
                      />
                      <span style={{ fontWeight: 'bold', fontSize: '0.8em', width: '8%' }}>
                        コアトップ案件:
                      </span>
                      <AvailabilityStatusSelect
                        onChange={handleCoreTopFlg}
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.7em',
                          width: '8%',
                          height: '80%'
                        }}
                        value={
                          buildingRegistration.dispCoreTopFlg !== undefined
                            ? [String(buildingRegistration.dispCoreTopFlg)]
                            : undefined
                        }
                        popupSettings={{ className: 'pcy-dropdownlist', popupClass: 'pcy-dropdownlist-innner' }}
                        clear={true}
                        className={'pcy-disabled-field'}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem col={1} row={3}>
                      <FiscalYearDropDownList
                        onYearChange={handleObtainFy}
                        fyddlStyle={{
                          fontWeight: 'bold',
                          fontSize: '0.7em',
                          width: '10%',
                          height: '80%'
                        }}
                        origin={-5}
                        range={16}
                        fyddlValue={
                          buildingRegistration.dispObtainFY !== null && buildingRegistration.dispObtainFY !== ''
                            ? Number(buildingRegistration.dispObtainFY)
                            : undefined
                        }
                        popupSettings={{ className: 'pcy-dropdownlist', popupClass: 'pcy-dropdownlist-innner' }}
                        clear={true}
                        fyTitle={'入手年度（入力必須）'}
                        className={'pcy-disabled-field'}
                      />
                      <PrefectureSelect
                        onChange={handlePrefecture}
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.7em',
                          width: '12%',
                          height: '80%'
                        }}
                        value={[buildingRegistration.dispPrefecture ?? '']}
                        popupSettings={{ className: 'pcy-dropdownlist', popupClass: 'pcy-dropdownlist-innner' }}
                        clear={true}
                        title={'都道府県'}
                        className={'pcy-disabled-field'}
                      />
                      <Input
                        onChange={handleCityName}
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.8em',
                          width: '9%',
                          height: '80%'
                        }}
                        value={buildingRegistration.dispCity ?? ''}
                        type={'text'}
                        title={'市区町村'}
                        placeholder={'市区町村'}
                        className={'pcy-disabled-field'}
                      />
                      <span style={{ fontWeight: 'bold', fontSize: '0.8em', paddingLeft: '2%' }}>
                        総工事金額（百万）：￥
                      </span>
                      <NumericTextBox
                        onChange={handleTotalCost}
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.8em',
                          width: '10%',
                          height: '80%'
                        }}
                        value={buildingRegistration.dispTotalCost}
                        spinners={false}
                        className={'pcy-disabled-field'}
                      />
                      <span style={{ fontWeight: 'bold', fontSize: '0.8em', paddingLeft: '2%' }}>
                        当社概算工事金額（百万）：￥
                      </span>
                      <NumericTextBox
                        onChange={handleOurCompanyTotalCost}
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.8em',
                          width: '10%',
                          height: '80%'
                        }}
                        value={buildingRegistration.dispOurCompanyTotalCost}
                        spinners={false}
                        className={'pcy-disabled-field'}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem col={1} row={4}>
                      <ConstrTypeSelect
                        onChange={handleConstrucionCd}
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.7em',
                          width: '10%',
                          height: '80%'
                        }}
                        value={[buildingRegistration.dispConstructionCd ?? '']}
                        popupSettings={{ className: 'pcy-dropdownlist', popupClass: 'pcy-dropdownlist-innner' }}
                        clear={true}
                        title={'工事種別'}
                        className={'pcy-disabled-field'}
                      />
                      <BuildingUsecaseDropDownList
                        onChange={handleArchitectureUse}
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.7em',
                          width: '18%',
                          height: '80%'
                        }}
                        value={buildingRegistration.architectureUse ?? ''}
                        popupSettings={{ className: 'pcy-dropdownlist', popupClass: 'pcy-dropdownlist-innner' }}
                        clear={true}
                        title={'建物用途（入力必須）'}
                        className={'pcy-disabled-field'}
                      />
                      <span style={{ fontWeight: 'bold', fontSize: '0.8em' }}>
                        （
                      </span>
                      <StructureTypeSelect
                        onChange={handleDispStructureUnderground}
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.7em',
                          width: '13%',
                          height: '80%'
                        }}
                        value={[buildingRegistration.dispStructureUnderGround ?? '']}
                        popupSettings={{ className: 'pcy-dropdownlist', popupClass: 'pcy-dropdownlist-innner' }}
                        clear={true}
                        title={'構造・地下（入力必須）'}
                        className={'pcy-disabled-field'}
                      />
                      <span style={{ fontWeight: 'bold', fontSize: '0.8em' }}>
                        /
                      </span>
                      <StructureTypeSelect
                        onChange={handleDispStructureGround}
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.7em',
                          width: '13%',
                          height: '80%'
                        }}
                        value={[buildingRegistration.dispStructureGround ?? '']}
                        popupSettings={{ className: 'pcy-dropdownlist', popupClass: 'pcy-dropdownlist-innner' }}
                        clear={true}
                        title={'構造・地上（入力必須）'}
                        className={'pcy-disabled-field'}
                      />
                      <span style={{ fontWeight: 'bold', fontSize: '0.8em' }}>
                        ）
                      </span>
                      <NumericTextBox
                        onChange={(e) => handleDispTotalFloorAreaM2AndTsubo(e)}
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.8em',
                          width: '10%',
                          height: '80%'
                        }}
                        value={buildingRegistration.dispTotalFloorAreaM2}
                        spinners={false}
                        title={'延床面積平米（入力必須）'}
                        placeholder={'延床面積平米（入力必須）'}
                        className={'pcy-disabled-field'}
                      />
                      <span style={{ fontWeight: 'bold', fontSize: '0.8em' }}>
                        ㎡ /
                      </span>
                      <NumericTextBox
                        readOnly
                        disabled={true}
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.8em',
                          width: '10%',
                          height: '80%'
                        }}
                        value={buildingRegistration.dispTotalFloorAreaTsubo}
                        spinners={false}
                        title={'延床面積坪（入力必須）'}
                        placeholder={'延床面積坪（入力必須）'}
                        className={'pcy-disabled-field'}
                      />
                      <span style={{ fontWeight: 'bold', fontSize: '0.8em' }}>
                        坪 -（
                      </span>
                      <NumericTextBox
                        onChange={handleDispFloorUnderGround}
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.8em',
                          width: '4%',
                          height: '80%'
                        }}
                        value={buildingRegistration.dispFloorUnderGround}
                        spinners={false}
                        title={'階数・地下（入力必須）'}
                        placeholder={'階数・地下（入力必須）'}
                        className={'pcy-disabled-field'}
                      />
                      <span style={{ fontWeight: 'bold', fontSize: '0.8em' }}>
                        /
                      </span>
                      <NumericTextBox
                        onChange={handleDispFloorGround}
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.8em',
                          width: '4%',
                          height: '80%'
                        }}
                        value={buildingRegistration.dispFloorGround}
                        spinners={false}
                        title={'階数・地上（入力必須）'}
                        placeholder={'階数・地上（入力必須）'}
                        className={'pcy-disabled-field'}
                      />
                      <span style={{ fontWeight: 'bold', fontSize: '0.8em' }}>
                        /
                      </span>
                      <NumericTextBox
                        onChange={handleDispFloorPenthouse}
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.8em',
                          width: '4%',
                          height: '80%'
                        }}
                        value={buildingRegistration.dispFloorPenthouse}
                        spinners={false}
                        title={'階数・塔屋（入力必須）'}
                        placeholder={'階数・塔屋（入力必須）'}
                        className={'pcy-disabled-field'}
                      />
                      <span style={{ fontWeight: 'bold', fontSize: '0.8em' }}>
                        ）
                      </span>
                    </GridLayoutItem>
                    <GridLayoutItem col={1} row={5}>
                      <span style={{ fontWeight: 'bold', fontSize: '0.8em' }}>
                        設備工事:
                      </span>
                      <AvailabilityStatusSelect
                        onChange={handleDispBuildingServicesWorkFlg}
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.7em',
                          width: '9.25%',
                          height: '80%'
                        }}
                        value={
                          buildingRegistration.dispBuildingServicesWorkFlg !==
                          undefined
                            ? [
                                String(
                                  buildingRegistration.dispBuildingServicesWorkFlg
                                )
                              ]
                            : undefined
                        }
                        popupSettings={{ className: 'pcy-dropdownlist', popupClass: 'pcy-dropdownlist-innner' }}
                        clear={true}
                        title={'設備工事'}
                        className={'pcy-disabled-field'}
                      />
                      <span style={{ fontWeight: 'bold', fontSize: '0.8em' }}>
                        杭打:
                      </span>
                      <AvailabilityStatusSelect
                        onChange={handlePiling}
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.7em',
                          width: '9.25%',
                          height: '80%'
                        }}
                        value={
                          buildingRegistration.piling !== undefined
                            ? [String(buildingRegistration.piling)]
                            : undefined
                        }
                        popupSettings={{ className: 'pcy-dropdownlist', popupClass: 'pcy-dropdownlist-innner' }}
                        clear={true}
                        title={'杭打（入力必須）'}
                        className={'pcy-disabled-field'}
                      />
                      <span style={{ fontWeight: 'bold', fontSize: '0.8em' }}>
                        {' '}
                        免震:
                      </span>
                      <AvailabilityStatusSelect
                        onChange={handleSeismicIsolation}
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.7em',
                          width: '9.25%',
                          height: '80%'
                        }}
                        value={
                          buildingRegistration.seismicIsolation !== undefined
                            ? [String(buildingRegistration.seismicIsolation)]
                            : undefined
                        }
                        popupSettings={{ className: 'pcy-dropdownlist', popupClass: 'pcy-dropdownlist-innner' }}
                        clear={true}
                        title={'免震（入力必須）'}
                        className={'pcy-disabled-field'}
                      />
                      <span style={{ fontWeight: 'bold', fontSize: '0.8em' }}>
                        {' '}
                      </span>
                      <UnderGroundConstrMethodSelect
                        onChange={handleTopDown}
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.7em',
                          width: '12.75%',
                          height: '80%'
                        }}
                        value={
                          buildingRegistration.topDownFlg !== undefined
                            ? [String(buildingRegistration.topDownFlg)]
                            : undefined
                        }
                        popupSettings={{ className: 'pcy-dropdownlist', popupClass: 'pcy-dropdownlist-innner' }}
                        title={'順打逆打（入力必須）'}
                        className={'pcy-disabled-field'}
                      />
                      <span style={{ fontWeight: 'bold', fontSize: '0.8em' }}>
                        {' '}
                        工法:
                      </span>
                      <Input
                        onChange={handleSpecialConstructionMethodCd}
                        value={
                          buildingRegistration.dispSpecialConstructionMethod ?? ''
                        }
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.8em',
                          width: '40%',
                          height: '80%'
                        }}
                        title={'工法'}
                        placeholder={'工法'}
                        className={'pcy-disabled-field'}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem col={1} colSpan={3} row={6}>
                      <FiscalMonthDropDownList
                        onYearChange={handleStartY}
                        onMonthChange={handleStartM}
                        fyddlStyle={{
                          fontWeight: 'bold',
                          fontSize: '0.7em',
                          width: '12%',
                          height: '80%'
                        }}
                        fmddlStyle={{
                          fontWeight: 'bold',
                          fontSize: '0.7em',
                          width: '12%',
                          height: '80%'
                        }}
                        fyddlValue={
                          buildingRegistration.startY !== undefined &&
                          buildingRegistration.startY !== ''
                            ? Number(buildingRegistration.startY)
                            : undefined
                          }
                        fmddlValue={
                          buildingRegistration.startM !== undefined &&
                          buildingRegistration.startM !== ''
                            ? Number(buildingRegistration.startM)
                            : undefined
                        }
                        origin={-10}
                        range={21}
                        noDiv
                        popupSettings={{ className: 'pcy-dropdownlist', popupClass: 'pcy-dropdownlist-innner' }}
                        clear={true}
                        fyTitle={'着工年（入力必須）'}
                        fmTitle={'着工月（入力必須）'}
                        className={'pcy-disabled-field'}
                        fmClassName={'pcy-disabled-field'}
                      />
                      <span style={{ fontWeight: 'bold', fontSize: '0.8em' }}>
                        -
                      </span>
                      <FiscalMonthDropDownList
                        onYearChange={handleCompletionY}
                        onMonthChange={handleCompletionM}
                        fyddlStyle={{
                          fontWeight: 'bold',
                          fontSize: '0.7em',
                          width: '12%',
                          height: '80%'
                        }}
                        fmddlStyle={{
                          fontWeight: 'bold',
                          fontSize: '0.7em',
                          width: '12%',
                          height: '80%'
                        }}
                        fyddlValue={
                          buildingRegistration.completionY !== undefined &&
                          buildingRegistration.completionY !== ''
                            ? Number(buildingRegistration.completionY)
                            : undefined
                        }
                        fmddlValue={
                          buildingRegistration.completionM !== undefined &&
                          buildingRegistration.completionM !== ''
                            ? Number(buildingRegistration.completionM)
                            : undefined
                        }
                        origin={-10}
                        range={31}
                        noDiv
                        popupSettings={{ className: 'pcy-dropdownlist', popupClass: 'pcy-dropdownlist-innner' }}
                        clear={true}
                        fyTitle={'竣工年（入力必須）'}
                        fmTitle={'竣工月（入力必須）'}
                        className={'pcy-disabled-field'}
                        fmClassName={'pcy-disabled-field'}
                      />
                      <NumericTextBox
                        onChange={handleSchedule}
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.8em',
                          width: '5%',
                          height: '80%'
                        }}
                        value={buildingRegistration.dispSchedule}
                        spinners={false}
                        title={'工期（入力必須）'}
                        placeholder={'工期（入力必須）'}
                        className={'pcy-disabled-field'}
                      />
                      <span style={{ fontWeight: 'bold', fontSize: '0.8em' }}>
                        ヶ月
                      </span>
                      <span style={{ marginLeft: '5px' }}></span>
                      <NumericTextBox
                        onChange={(e) => handleDispSiteAreaM2AndTsubo(e)}
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.8em',
                          width: '10%',
                          height: '80%'
                        }}
                        value={buildingRegistration.dispSiteAreaM2}
                        spinners={false}
                        title={'敷地面積平米'}
                        placeholder={'敷地面積平米'}
                        className={'pcy-disabled-field'}
                      />
                      <span style={{ fontWeight: 'bold', fontSize: '0.8em' }}>
                        ㎡ /
                      </span>
                      <NumericTextBox
                        readOnly
                        disabled={true}
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.8em',
                          width: '10%',
                          height: '80%'
                        }}
                        value={buildingRegistration.dispSiteAreaTsubo}
                        spinners={false}
                        title={'敷地面積坪'}
                        placeholder={'敷地面積坪'}
                        className={'pcy-disabled-field'}
                      />
                      <span style={{ fontWeight: 'bold', fontSize: '0.8em' }}>
                        坪
                      </span>
                      <span style={{ marginLeft: '5px' }}></span>
                      <NumericTextBox
                        onChange={handleSagyosyoCd}
                        value={
                          buildingRegistration.dispSagyosyoCd !== null
                            ? Number(buildingRegistration.dispSagyosyoCd)
                            : null
                        }
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.8em',
                          width: '11%',
                          height: '80%'
                        }}
                        format={'#'}
                        spinners={false}
                        title={'作業所コード'}
                        placeholder={'作業所コード'}
                        className={'pcy-disabled-field'}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem col={1} row={7}>
                      <span style={{ fontWeight: 'bold', fontSize: '0.8em' }}>
                        備考:
                      </span>
                      <TextArea
                        onChange={handleRemark}
                        value={buildingRegistration.remark ?? ''}
                      />
                    </GridLayoutItem>
                  </GridLayout>
                </GridLayoutItem>

                <GridLayoutItem col={2}>
                  <GridLayout
                    gap={{ cols: 1 }}
                    cols={[{ width: '25%' }, { width: '75%' }]}
                  >
                    <GridLayoutItem col={1} row={1}>
                      <span style={{ fontSize: '0.8em' }}>工事部</span>
                    </GridLayoutItem>
                    <GridLayoutItem col={2} row={1}>
                      <Input
                        onChange={handleConstructionSectionNm}
                        style={{
                          fontWeight: 'normal',
                          fontSize: '0.8em',
                          width: '54%',
                          height: '80%'
                        }}
                        value={buildingRegistration.dispConstructionSectionNm ?? ''}
                        type={'text'}
                        title={'工事部名'}
                        placeholder={'工事部名'}
                        className={'pcy-disabled-field'}
                      />
                      <span style={{ fontSize: '0.8em' }}>）</span>
                      <Input
                        onChange={handleChiefPersonNm}
                        style={{
                          fontWeight: 'normal',
                          fontSize: '0.8em',
                          width: '40%',
                          height: '80%'
                        }}
                        value={buildingRegistration.dispChiefPersonNm ?? ''}
                        type={'text'}
                        title={'所長担当者名'}
                        placeholder={'所長担当者名'}
                        className={'pcy-disabled-field'}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem col={2} row={2}>
                      <Input
                        onChange={handleChiefPersonContacts}
                        style={{
                          fontWeight: 'normal',
                          fontSize: '0.8em',
                          width: '100%',
                          height: '80%'
                        }}
                        value={buildingRegistration.dispChiefPersonContacts ?? ''}
                        type={'text'}
                        title={'所長担当者連絡先'}
                        placeholder={'所長担当者連絡先'}
                        className={'pcy-disabled-field'}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem col={1} row={3}>
                      <span style={{ fontSize: '0.8em' }}>本社営業部</span>
                    </GridLayoutItem>
                    <GridLayoutItem col={2} row={3}>
                      <Input
                        onChange={handleHqSalesSectionNm}
                        style={{
                          fontWeight: 'normal',
                          fontSize: '0.8em',
                          width: '54%',
                          height: '80%'
                        }}
                        value={buildingRegistration.dispHqSalesSectionNm ?? ''}
                        type={'text'}
                        title={'本社営業部名'}
                        placeholder={'本社営業部名'}
                        className={'pcy-disabled-field'}
                      />
                      <span style={{ fontSize: '0.8em' }}>）</span>
                      <Input
                        onChange={handleHqSalesPersonNm}
                        style={{
                          fontWeight: 'normal',
                          fontSize: '0.8em',
                          width: '40%',
                          height: '80%'
                        }}
                        value={buildingRegistration.dispHqSalesPersonNm ?? ''}
                        type={'text'}
                        title={'本社営業担当者名'}
                        placeholder={'本社営業担当者名'}
                        className={'pcy-disabled-field'}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem col={2} row={4}>
                      <Input
                        onChange={handleHqSalesPersonContacts}
                        style={{
                          fontWeight: 'normal',
                          fontSize: '0.8em',
                          width: '100%',
                          height: '80%'
                        }}
                        value={buildingRegistration.dispHqSalesPersonContacts ?? ''}
                        type={'text'}
                        title={'本社営業担当者連絡先'}
                        placeholder={'本社営業担当者連絡先'}
                        className={'pcy-disabled-field'}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem col={1} row={5}>
                      <span style={{ fontSize: '0.8em' }}>支店営業部</span>
                    </GridLayoutItem>
                    <GridLayoutItem col={2} row={5}>
                      <Input
                        onChange={handleBranchSalesSectionNm}
                        style={{
                          fontWeight: 'normal',
                          fontSize: '0.8em',
                          width: '54%',
                          height: '80%'
                        }}
                        value={buildingRegistration.dispBranchSalesSectionNm ?? ''}
                        type={'text'}
                        title={'支店営業部名'}
                        placeholder={'支店営業部名'}
                        className={'pcy-disabled-field'}
                      />
                      <span style={{ fontSize: '0.8em' }}>）</span>
                      <Input
                        onChange={handleBranchSalesPersonNm}
                        style={{
                          fontWeight: 'normal',
                          fontSize: '0.8em',
                          width: '40%',
                          height: '80%'
                        }}
                        value={buildingRegistration.dispBranchSalesPersonNm ?? ''}
                        type={'text'}
                        title={'支店営業担当者名'}
                        placeholder={'支店営業担当者名'}
                        className={'pcy-disabled-field'}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem col={2} row={6}>
                      <Input
                        onChange={handleBranchSalesPersonContacts}
                        style={{
                          fontWeight: 'normal',
                          fontSize: '0.8em',
                          width: '100%',
                          height: '80%'
                        }}
                        value={buildingRegistration.dispBranchSalesPersonContacts ?? ''}
                        type={'text'}
                        title={'支店営業担当者連絡先'}
                        placeholder={'支店営業担当者連絡先'}
                        className={'pcy-disabled-field'}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem col={1} row={7}>
                      <span style={{ fontSize: '0.8em' }}>発注者</span>
                    </GridLayoutItem>
                    <GridLayoutItem col={2} row={7}>
                      <Input
                        onChange={handleCustomerNm}
                        style={{
                          fontWeight: 'normal',
                          fontSize: '0.8em',
                          width: '50%',
                          height: '80%'
                        }}
                        value={buildingRegistration.dispCustomerNm ?? ''}
                        type={'text'}
                        title={'発注者名'}
                        placeholder={'発注者名'}
                        className={'pcy-disabled-field'}
                      />
                      <Input
                        onChange={handleCollabCustomerNm}
                        style={{
                          fontWeight: 'normal',
                          fontSize: '0.8em',
                          width: '50%',
                          height: '80%'
                        }}
                        value={buildingRegistration.dispCollabCustomerNm ?? ''}
                        type={'text'}
                        title={'共同発注者名'}
                        placeholder={'共同発注者名'}
                        className={'pcy-disabled-field'}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem col={1} row={8}>
                      <span style={{ fontSize: '0.8em' }}>設計</span>
                    </GridLayoutItem>
                    <GridLayoutItem col={2} row={8}>
                      <DesignClassificationSelect
                        onChange={handleDesignKbnNm}
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.7em',
                          width: '100%',
                          height: '80%'
                        }}
                        value={[buildingRegistration.dispDesignKbn ?? '']}
                        popupSettings={{ className: 'pcy-dropdownlist', popupClass: 'pcy-dropdownlist-innner' }}
                        clear={true}
                        title={'設計区分'}
                        className={'pcy-disabled-field'}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem col={2} row={9}>
                      <Input
                        onChange={handleInputDesignerNm}
                        style={{
                          fontWeight: 'normal',
                          fontSize: '0.8em',
                          width: '50%',
                          height: '80%'
                        }}
                        value={buildingRegistration.dispDesignerNm ?? ''}
                        type={'text'}
                        title={'設計者名'}
                        placeholder={'設計者名'}
                        className={'pcy-disabled-field'}
                      />
                    </GridLayoutItem>
                  </GridLayout>
                </GridLayoutItem>
              </GridLayout>
            </CardBody>
          </Card>
          <GridLayout style={{ marginTop: '2%' }}>
            <GridLayoutItem>
              <span style={{ fontWeight: 'bold' }}>修正コメント</span>
              <TextArea
                onChange={handleFixComment}
                value={buildingRegistration.fixComment ?? ''}
              ></TextArea>
            </GridLayoutItem>
          </GridLayout>
        </Tooltip>
      </>
    </Contents>
  )
}
